import React from 'react';
import styled from '@emotion/styled';
import parseHtml from 'html-react-parser';
import innerText from 'react-innertext';
import { OutboundLink, routes } from 'gatsby-theme-apollo';
import compute from '../../util/color';

import { Card, CardTitle, CardDescription, CardByLine, CardTimeStamp, Cards } from './cards';
import TagBox from '../content/tags';
import { LimitReducer } from '../limit/limiter';
import useLimitedSet from '../../hooks/use-limit';
import EntityThumbnail from '../entities/entity-thumbnail';

const TierPreview = styled.div({
  borderBottomLeftRadius: `5px`,
  borderBottomRightRadius: `5px`,
  display: `flex`,
  position: `relative`,
  height: 'auto',
  justifyContent: `center`,
  alignItems: `stretch`
});

const TierPreviewThumbBox = styled.div`
  width: 100%;
  max-height: 180px;
  max-width: 130px;
  align-items: center;
  flex: 1;
  flex-grow: 1;
  flex-shrink: 0;
  flex-direction: column;
  display: flex;
  position: relative;
`;

const TierPreviewRemainderText = styled.div`
  font-weight: bold;
  color: ${props => compute({ value: props.tierIndex, maxValue: 3 })};
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 6px 0px 10px 0px;
`;

const TierName = styled.div`
  font-weight: bold;
  line-height: 18px;
  opacity: 0.75;
`;

const EntityCount = styled.div`
  font-weight: normal;
  opacity: 0.5;
  line-height: 16px;
`;

const TierListPreviewCards = ({
  tierListPreviews,
  limit,
  showInterval = 12,
  showGameName,
  disableShowMore,
  entityName
}) => {
  const [tierListPreviewsToDisplay, currentLimit, increaseLimit] = useLimitedSet({
    initialLimit: limit,
    set: tierListPreviews,
    interval: showInterval,
    contentType: 'tier_list'
  });

  return (
    <>
      <Cards>
        {tierListPreviewsToDisplay &&
          tierListPreviewsToDisplay.map(tierListPreview => {
            return (
              <TierListPreviewCard
                tierListPreview={tierListPreview}
                showGameName={showGameName}
                entityName={entityName}
                key={tierListPreview.permalink}
              />
            );
          })}
      </Cards>
      {!disableShowMore && (
        <LimitReducer
          limit={currentLimit}
          setSize={tierListPreviews.length}
          onIncreaseLimit={increaseLimit}
          showInterval={showInterval}
        />
      )}
    </>
  );
};

const sanitizeTierName = (name) => {
  const maxLineLength = 13;
  var parsedName = parseHtml(name);
  if (Array.isArray(parsedName))
  {
    var firstLine = innerText(parsedName[0]);
    if (firstLine.length > maxLineLength)
      return firstLine;

    var secondLine = innerText(parsedName[1]);
    if (secondLine.length > maxLineLength)
      secondLine = secondLine.substr(0, maxLineLength - 3) + "...";
    return <><div>{firstLine}</div><div>{secondLine}</div></>;
  }

  return innerText(parsedName);
};

const TierListPreviewCard = ({ tierListPreview, showGameName, entityName }) => {
  const {
    author,
    title,
    description,
    permalink,
    tiers,
    game,
    lastPublish,
    featured,
    featuredTier
  } = tierListPreview;

  const descriptionPreviewCharacterLimit = 85;
  const descriptionPreview =
    description && description.substring(0, descriptionPreviewCharacterLimit);
  const isDescriptionPastLimit = description && description.length > descriptionPreviewCharacterLimit;

  const tags = [];
  if (showGameName) {
    if (game && game.name) {
      tags.push({ text: game.name });
    }
  }

  if (featured) {
    tags.push({ text: 'Featured', color: `orange` });
  }

  if (featuredTier) {
    tags.push({ text: `${entityName} Tier: ${featuredTier}`, color: `white` });
  }

  return (
    <Card>
      <OutboundLink href={routes.SavedTierList(game && game.name, permalink)}>
        <CardTitle>{title}</CardTitle>
        {author && (
          <CardByLine>
            <span>by {author.name}</span>
          </CardByLine>
        )}
        {description && (
          <CardDescription>
            {descriptionPreview}
            {isDescriptionPastLimit ? `...` : ''}
          </CardDescription>
        )}

        <TierPreview>
          {tiers &&
            tiers.map(({ name, numCharacters, character }, index) => {
              return (
                <TierPreviewThumbBox key={index}>
                  <EntityThumbnail 
                    imgSrc={character.thumbnailURI} 
                  />
                  <TierPreviewRemainderText tierIndex={index} className="preview">
                    <TierName>{name && sanitizeTierName(name)}</TierName>
                    {numCharacters > 1 && (
                      <EntityCount>
                        {numCharacters - 1}
                        {` `}
                        more
                      </EntityCount>
                    )}
                  </TierPreviewRemainderText>
                </TierPreviewThumbBox>
              );
            })}
        </TierPreview>
        <TagBox tags={tags} />
        <CardTimeStamp updatedAt={lastPublish} />
      </OutboundLink>
    </Card>
  );
};

export default TierListPreviewCards;
